/**
 * NG-ZORRO ACCESSIBILITY WRAPPER
 * Aggiunge ARIA support ai componenti NG-Zorro
 * Standard: WCAG 2.1 Level AA
 * 
 * Uso: <nz-dropdown nzAccessible>
 */

import { Directive, ElementRef, Input, HostListener, AfterViewInit, OnDestroy } from '@angular/core';

/**
 * ACCESSIBILITY WRAPPER PER: nz-dropdown
 * 
 * Aggiungi a: <nz-dropdown nzAccessible>
 */
@Directive({
  selector: '[nzAccessible]',
  standalone: true,
})
export class NzDropdownAccessibilityDirective implements AfterViewInit {
  @Input() ariaLabel?: string;
  @Input() ariaDescribedBy?: string;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const trigger = this.el.nativeElement.querySelector('[nz-dropdown]');
    const menu = this.el.nativeElement.querySelector('[nz-dropdown-menu]');

    if (trigger) {
      // Aggiungi ARIA attributes al trigger
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      
      if (this.ariaLabel) {
        trigger.setAttribute('aria-label', this.ariaLabel);
      }

      // Aggiungi role al menu quando visibile
      if (menu) {
        menu.setAttribute('role', 'menu');
      }

      // Monitora apertura/chiusura del menu
      this.monitorMenuState(trigger, menu);
    }
  }

  private monitorMenuState(trigger: HTMLElement, menu: HTMLElement): void {
    // Observer per detectare quando menu è visibile
    const observer = new MutationObserver(() => {
      const isVisible = menu.offsetParent !== null;
      trigger.setAttribute('aria-expanded', isVisible ? 'true' : 'false');
    });

    observer.observe(menu, { attributes: true, attributeFilter: ['style', 'class'] });
  }

  /**
   * WCAG 2.1.1 - Keyboard
   * Supporta Escape per chiudere il dropdown
   */
  @HostListener('keydown.escape')
  closeDropdown() {
    // NG-Zorro gestisce automaticamente l'Escape
    // Ma possiamo aggiungere logica custom se necessario
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-modal
 * 
 * Aggiungi a: <nz-modal [nzAccessible]="true">
 */
@Directive({
  selector: '[nzModal][nzAccessible]',
  standalone: true,
})
export class NzModalAccessibilityDirective implements AfterViewInit {
  @Input() nzAccessible: boolean = true;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    if (!this.nzAccessible) return;

    const modalContent = this.el.nativeElement.querySelector('.ant-modal');
    
    if (modalContent) {
      // Aggiungi role dialog (WCAG 4.1.2)
      modalContent.setAttribute('role', 'dialog');
      modalContent.setAttribute('aria-modal', 'true');

      // Trova e associa titolo (aria-labelledby)
      const title = modalContent.querySelector('.ant-modal-title');
      if (title) {
        const titleId = title.id || this.generateId('modal-title');
        title.id = titleId;
        modalContent.setAttribute('aria-labelledby', titleId);
      }

      // Trova e associa descrizione (aria-describedby) - optional
      const body = modalContent.querySelector('.ant-modal-body');
      if (body) {
        const bodyId = body.id || this.generateId('modal-description');
        body.id = bodyId;
        modalContent.setAttribute('aria-describedby', bodyId);
      }
    }
  }

  private generateId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-input
 * 
 * Aggiungi a: <nz-form-item>
 *              <label nzLabel [nzFor]="'email'">Email:</label>
 *              <input nz-input id="email" nzAccessible>
 */
@Directive({
  selector: '[nz-input][nzAccessible]',
  standalone: true,
})
export class NzInputAccessibilityDirective implements AfterViewInit {
  @Input() ariaDescribedBy?: string;
  @Input() ariaInvalid?: boolean;
  @Input() ariaRequired?: boolean;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const input = this.el.nativeElement;

    // Aggiungi ARIA attributes
    if (this.ariaDescribedBy) {
      input.setAttribute('aria-describedby', this.ariaDescribedBy);
    }

    if (this.ariaInvalid !== undefined) {
      input.setAttribute('aria-invalid', this.ariaInvalid ? 'true' : 'false');
    }

    if (this.ariaRequired !== undefined) {
      input.setAttribute('aria-required', this.ariaRequired ? 'true' : 'false');
    }

    // Monitora stato di validazione
    this.monitorValidation(input);
  }

  private monitorValidation(input: HTMLElement): void {
    const formItem = input.closest('.ant-form-item');
    if (!formItem) return;

    const observer = new MutationObserver(() => {
      const hasError = formItem.classList.contains('ant-form-item-has-error');
      input.setAttribute('aria-invalid', hasError ? 'true' : 'false');
    });

    observer.observe(formItem, { attributes: true, attributeFilter: ['class'] });
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-table
 * 
 * Aggiungi a: <nz-table nzAccessible>
 */
@Directive({
  selector: '[nz-table][nzAccessible]',
  standalone: true,
})
export class NzTableAccessibilityDirective implements AfterViewInit {
  @Input() nzAccessible: boolean = true;
  @Input() tableLabel?: string;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    if (!this.nzAccessible) return;

    const table = this.el.nativeElement.querySelector('table');
    if (!table) return;

    // Aggiungi role grid (WCAG 1.3.1)
    table.setAttribute('role', 'grid');
    
    if (this.tableLabel) {
      table.setAttribute('aria-label', this.tableLabel);
    }

    // Aggiungi scope a header (WCAG 1.3.1)
    const thHeaders = table.querySelectorAll('thead th');
    thHeaders.forEach((th: HTMLElement) => {
      th.setAttribute('scope', 'col');
      th.setAttribute('role', 'columnheader');
    });

    // Aggiungi role gridcell alle celle
    const cells = table.querySelectorAll('tbody td');
    cells.forEach((cell: HTMLElement) => {
      cell.setAttribute('role', 'gridcell');
    });

    // Aggiungi aria-rowindex alle righe
    const rows = table.querySelectorAll('tbody tr');
    rows.forEach((row: HTMLElement, index: number) => {
      row.setAttribute('aria-rowindex', (index + 2).toString()); // 2 perché 1 è header
    });
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-form-item + input
 * 
 * Wrapper completo che associa label a input
 * Aggiungi a: <div nzFormItemAccessible>
 *              <label>Email</label>
 *              <input>
 */
@Directive({
  selector: '[nzFormItemAccessible]',
  standalone: true,
})
export class NzFormItemAccessibilityDirective implements AfterViewInit {
  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const formItem = this.el.nativeElement;
    const label = formItem.querySelector('label');
    const input = formItem.querySelector('input, textarea, select');

    if (label && input) {
      // Se label non ha for, aggiungilo
      if (!label.getAttribute('for')) {
        const inputId = input.id || this.generateId('form-input');
        input.id = inputId;
        label.setAttribute('for', inputId);
      }

      // Associa error message se presente
      const errorMsg = formItem.querySelector('.ant-form-item-explain-error');
      if (errorMsg) {
        const errorId = errorMsg.id || this.generateId('error-msg');
        errorMsg.id = errorId;
        input.setAttribute('aria-describedby', errorId);
      }

      // Monitora validazione
      this.monitorValidation(formItem, input);
    }
  }

  private monitorValidation(formItem: HTMLElement, input: HTMLElement): void {
    const observer = new MutationObserver(() => {
      const hasError = formItem.classList.contains('ant-form-item-has-error');
      const isRequired = input.hasAttribute('required') || input.getAttribute('aria-required') === 'true';
      
      input.setAttribute('aria-invalid', hasError ? 'true' : 'false');
      input.setAttribute('aria-required', isRequired ? 'true' : 'false');
    });

    observer.observe(formItem, { attributes: true, attributeFilter: ['class'] });
  }

  private generateId(prefix: string): string {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-select
 * 
 * Aggiungi a: <nz-select nzAccessible [ariaLabel]="'Scegli opzione'">
 */
@Directive({
  selector: '[nz-select][nzAccessible]',
  standalone: true,
})
export class NzSelectAccessibilityDirective implements AfterViewInit {
  @Input() ariaLabel?: string;
  @Input() ariaDescribedBy?: string;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const trigger = this.el.nativeElement.querySelector('.ant-select-selector');
    
    if (trigger) {
      // Aggiungi ARIA combobox (WCAG 1.3.1)
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');

      if (this.ariaLabel) {
        trigger.setAttribute('aria-label', this.ariaLabel);
      }

      if (this.ariaDescribedBy) {
        trigger.setAttribute('aria-describedby', this.ariaDescribedBy);
      }

      // Monitora apertura dropdown
      this.monitorDropdown(trigger);
    }
  }

  private monitorDropdown(trigger: HTMLElement): void {
    const observer = new MutationObserver(() => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true' || 
                     trigger.classList.contains('ant-select-open');
      trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    observer.observe(trigger, { attributes: true, attributeFilter: ['class', 'aria-expanded'] });
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-button
 * 
 * Aggiungi a: <button nz-button nzAccessible [ariaLabel]="'Chiudi modal'">
 *              <i nz-icon nzType="close"></i>
 */
@Directive({
  selector: '[nz-button][nzAccessible]',
  standalone: true,
})
export class NzButtonAccessibilityDirective implements AfterViewInit {
  @Input() ariaLabel?: string;
  @Input() ariaPressed?: boolean;
  @Input() ariaDescribedBy?: string;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const button = this.el.nativeElement;

    // Aggiungi role button (WCAG 4.1.2)
    if (!button.getAttribute('role')) {
      button.setAttribute('role', 'button');
    }

    if (this.ariaLabel) {
      button.setAttribute('aria-label', this.ariaLabel);
    }

    if (this.ariaPressed !== undefined) {
      button.setAttribute('aria-pressed', this.ariaPressed ? 'true' : 'false');
    }

    if (this.ariaDescribedBy) {
      button.setAttribute('aria-describedby', this.ariaDescribedBy);
    }

    // Aggiungi supporto spacebar per attivazione (WCAG 2.1.1)
    button.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        button.click();
      }
    });
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-icon
 * 
 * Aggiungi a: <i nz-icon nzType="close" nzAccessible [ariaLabel]="'Chiudi'"></i>
 * O per icone decorative: <i nz-icon nzType="check" nzAccessible ariaHidden="true"></i>
 */
@Directive({
  selector: '[nz-icon][nzAccessible]',
  standalone: true,
})
export class NzIconAccessibilityDirective implements AfterViewInit {
  @Input() ariaLabel?: string;
  @Input() ariaHidden?: boolean;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const icon = this.el.nativeElement;

    if (this.ariaHidden) {
      // Icona decorativa - nascondi da screen reader
      icon.setAttribute('aria-hidden', 'true');
    } else if (this.ariaLabel) {
      // Icona funzionale - fornisci etichetta
      icon.setAttribute('role', 'img');
      icon.setAttribute('aria-label', this.ariaLabel);
    }
  }
}

/**
 * ACCESSIBILITY WRAPPER PER: nz-menu
 * 
 * Aggiungi a: <ul nz-menu nzAccessible>
 */
@Directive({
  selector: '[nz-menu][nzAccessible]',
  standalone: true,
})
export class NzMenuAccessibilityDirective implements AfterViewInit {
  @Input() nzAccessible: boolean = true;
  @Input() menuLabel?: string;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    if (!this.nzAccessible) return;

    const menu = this.el.nativeElement;
    
    // Aggiungi role menu (WCAG 1.3.1)
    menu.setAttribute('role', 'menu');
    
    if (this.menuLabel) {
      menu.setAttribute('aria-label', this.menuLabel);
    }

    // Aggiungi role menuitem a tutti i menu item
    const items = menu.querySelectorAll('[nz-menu-item], li');
    items.forEach((item: HTMLElement) => {
      if (!item.getAttribute('role')) {
        item.setAttribute('role', 'menuitem');
      }
    });
  }
}

@Directive({
  selector: 'nz-time-picker',
  standalone: true,
})
export class NzTimePickerAccessibilityDirective implements AfterViewInit, OnDestroy {
  private bodyObserver?: MutationObserver;
  private activePanel: HTMLElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private cellIdCounter = 0;

  private readonly inputKeydownHandler = (event: KeyboardEvent): void => this.handleInputKeydown(event);
  private readonly inputBlurHandler = (): void => this.clearHighlight();

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.inputEl = this.el.nativeElement.querySelector('input');
    if (this.inputEl) {
      this.inputEl.setAttribute('aria-haspopup', 'listbox');
      this.inputEl.addEventListener('keydown', this.inputKeydownHandler);
      this.inputEl.addEventListener('blur', this.inputBlurHandler);
    }

    this.bodyObserver = new MutationObserver(() => this.tryAttachToPanel());
    this.bodyObserver.observe(document.body, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.bodyObserver?.disconnect();
    this.inputEl?.removeEventListener('keydown', this.inputKeydownHandler);
    this.inputEl?.removeEventListener('blur', this.inputBlurHandler);
    this.activePanel = null;
  }

  private tryAttachToPanel(): void {
    const panel = document.querySelector('.ant-picker-time-panel:not([data-accessible-bound])') as HTMLElement | null;
    if (!panel) return;

    panel.setAttribute('data-accessible-bound', 'true');
    panel.setAttribute('role', 'listbox');
    this.activePanel = panel;
    this.highlightCell(this.getSelectedOrFirstCell(panel));
  }

  private handleInputKeydown(event: KeyboardEvent): void {
    if (!this.activePanel || !document.body.contains(this.activePanel)) {
      this.activePanel = null;
      return;
    }

    const columns = Array.from(this.activePanel.querySelectorAll('.ant-picker-time-panel-column')) as HTMLElement[];
    const currentCell = this.activePanel.querySelector('.keyboard-focus-cell') as HTMLElement | null;
    const currentColumn = currentCell?.closest('.ant-picker-time-panel-column') as HTMLElement | null;
    const columnIndex = currentColumn ? columns.indexOf(currentColumn) : 0;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveWithinColumn(columns[columnIndex] ?? columns[0], event.key === 'ArrowDown' ? 1 : -1);
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      const nextIndex = event.key === 'ArrowRight' ? Math.min(columnIndex + 1, columns.length - 1) : Math.max(columnIndex - 1, 0);
      this.highlightCell(this.getSelectedOrFirstCell(columns[nextIndex]));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      currentCell?.click();
    }
  }

  private moveWithinColumn(column: HTMLElement, direction: 1 | -1): void {
    const cells = this.getEnabledCells(column);
    if (cells.length === 0) return;

    const current = column.querySelector('.keyboard-focus-cell') as HTMLElement | null;
    const currentIndex = current ? cells.indexOf(current) : -1;
    const nextCell = cells[(currentIndex + direction + cells.length) % cells.length];
    nextCell.click();
    this.highlightCell(nextCell);
    nextCell.scrollIntoView({ block: 'nearest' });
  }

  private highlightCell(cell: HTMLElement | null): void {
    this.activePanel?.querySelectorAll('.keyboard-focus-cell').forEach((element) => element.classList.remove('keyboard-focus-cell'));
    if (!cell) {
      this.inputEl?.removeAttribute('aria-activedescendant');
      return;
    }

    if (!cell.id) cell.id = `time-panel-cell-${++this.cellIdCounter}-${Date.now()}`;
    cell.classList.add('keyboard-focus-cell');
    this.inputEl?.setAttribute('aria-activedescendant', cell.id);
  }

  private clearHighlight(): void {
    this.activePanel?.querySelectorAll('.keyboard-focus-cell').forEach((element) => element.classList.remove('keyboard-focus-cell'));
    this.inputEl?.removeAttribute('aria-activedescendant');
    this.activePanel = null;
  }

  private getSelectedOrFirstCell(scope: HTMLElement): HTMLElement | null {
    const selected = scope.querySelector('.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner') as HTMLElement | null;
    return selected ?? this.getEnabledCells(scope)[0] ?? null;
  }

  private getEnabledCells(scope: HTMLElement): HTMLElement[] {
    return Array.from(scope.querySelectorAll('.ant-picker-time-panel-cell:not(.ant-picker-time-panel-cell-disabled) .ant-picker-time-panel-cell-inner'));
  }
}

@Directive({
  selector: 'ds-datepicker',
  standalone: true,
})
export class DsDatepickerOverlayFixDirective implements AfterViewInit, OnDestroy {
  private observer?: MutationObserver;

  ngAfterViewInit(): void {
    this.observer = new MutationObserver(() => this.bringOverlayToFront());
    this.observer.observe(document.body, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private bringOverlayToFront(): void {
    const dropdown = document.querySelector('.ant-picker-dropdown:not([data-zindex-fixed])') as HTMLElement | null;
    if (!dropdown) return;

    dropdown.setAttribute('data-zindex-fixed', 'true');
    const container = dropdown.closest('.cdk-overlay-container') as HTMLElement | null;
    const pane = dropdown.closest('.cdk-overlay-pane') as HTMLElement | null;
    if (container) container.style.zIndex = '2000';
    if (pane) pane.style.zIndex = '2000';
  }
}
